import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import {
  encodeApiError,
  TApiErrorResponse,
} from '@sandbox-platform/shared-ui';

import { ReferralSourceChannelApiService } from '../../../../services/referral-source-channel-api.service';
import {
  CreateReferralSourceChannelDTO,
  ReferralSourceChannelDTO,
  UpdateReferralSourceChannelDTO,
} from '../../../../types/referral-source-channel.types';

type DetailMode = 'create' | 'edit';

interface ReferralSourceChannelDetailState {
  sourceChannel: ReferralSourceChannelDTO | null;
  loading: boolean;
  error: string | TApiErrorResponse | null;
  mode: DetailMode;
}

const initialState: ReferralSourceChannelDetailState = {
  sourceChannel: null,
  loading: false,
  error: null,
  mode: 'create',
};

export const ReferralSourceChannelDetailStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    isCreateMode: computed(() => store.mode() === 'create'),
    isLoading: computed(() => store.loading()),
  })),
  withMethods((store) => {
    const api = inject(ReferralSourceChannelApiService);

    return {
      initializeForCreate(): void {
        patchState(store, {
          sourceChannel: null,
          mode: 'create',
          error: null,
        });
      },

      async load(id: string): Promise<ReferralSourceChannelDTO | null> {
        patchState(store, { loading: true, error: null, mode: 'edit' });

        try {
          const response = await firstValueFrom(api.getById(id));
          patchState(store, { sourceChannel: response.data });
          return response.data;
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to load source channel'),
          });
          return null;
        } finally {
          patchState(store, { loading: false });
        }
      },

      async create(
        dto: CreateReferralSourceChannelDTO
      ): Promise<ReferralSourceChannelDTO | null> {
        patchState(store, { loading: true, error: null });

        try {
          const response = await firstValueFrom(api.create(dto));
          patchState(store, {
            sourceChannel: response.data,
            mode: 'edit',
          });
          return response.data;
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to create source channel'),
          });
          return null;
        } finally {
          patchState(store, { loading: false });
        }
      },

      async update(
        id: string,
        dto: UpdateReferralSourceChannelDTO
      ): Promise<ReferralSourceChannelDTO | null> {
        patchState(store, { loading: true, error: null });

        try {
          const response = await firstValueFrom(api.update(id, dto));
          patchState(store, { sourceChannel: response.data });
          return response.data;
        } catch (error) {
          patchState(store, {
            error: encodeApiError(error, 'Failed to update source channel'),
          });
          return null;
        } finally {
          patchState(store, { loading: false });
        }
      },

      clearError(): void {
        patchState(store, { error: null });
      },
    };
  })
);

export const injectReferralSourceChannelDetailStore = () =>
  inject(ReferralSourceChannelDetailStore);
